import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { openLink } from '@tma.js/sdk-react';
import { ExternalLink, Trophy, Users } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import { ChessStatusBadge } from '@/components/chess/ChessStatusBadge';
import {
  useGetTournamentQuery,
  useGetTournamentResultsQuery,
} from '@/services/qchessApi';
import { classifyQchessError, qchessErrorKey } from '@/services/qchessErrors';
import type { TournamentStanding } from '@/types/qchess';

/**
 * Chess tournament detail — info card, "Open on Lichess" deeplink, an embedded
 * Lichess view, and the standings table.
 *
 * The standings come from `GET /v1/tournaments/:id/results` once available;
 * before that we fall back to the `finalStandings` already on the tournament
 * document (empty until the tournament finishes).
 */
export function ChessTournamentDetail() {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();
  const { id = '' } = useParams<{ id: string }>();

  const tourQuery = useGetTournamentQuery(id, { skip: !id });
  const tour = tourQuery.data;

  // Results: only meaningful once the tournament has produced standings. We
  // fetch unconditionally (the endpoint returns an empty list otherwise) and
  // prefer it over the document's finalStandings when non-empty.
  const resultsQuery = useGetTournamentResultsQuery(id, { skip: !id });
  const standings: TournamentStanding[] =
    resultsQuery.data?.results?.length
      ? resultsQuery.data.results
      : tour?.finalStandings ?? [];

  if (tourQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (tourQuery.error || !tour) {
    return (
      <div className="px-4 pt-6">
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {t(qchessErrorKey(classifyQchessError(tourQuery.error)))}
        </p>
      </div>
    );
  }

  const clockLabel = `${Math.round(tour.clockLimitSeconds / 60)}+${tour.clockIncrementSeconds}`;

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Trophy aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-xl font-bold text-fg">{tour.name}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-fg-muted">
          <ChessStatusBadge status={tour.status} />
          <span className="uppercase">{tour.kind}</span>
          <span aria-hidden>·</span>
          <span>{clockLabel}</span>
          <span aria-hidden>·</span>
          <span className="uppercase">{tour.variant}</span>
          <span aria-hidden>·</span>
          <span>{tour.rated ? t('tournamentDetail.rated') : t('tournamentDetail.casual')}</span>
          <span aria-hidden>·</span>
          <span className="flex items-center gap-1">
            <Users aria-hidden className="h-3 w-3" />
            {t('tournaments.players', { count: tour.nbPlayers })}
          </span>
        </div>
      </header>

      {/* Open on Lichess */}
      <button
        type="button"
        onClick={() => {
          haptics.impact('medium');
          openLink.ifAvailable(tour.lichessUrl);
        }}
        className="flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
      >
        <ExternalLink aria-hidden className="h-4 w-4" />
        {t('tournamentDetail.openOnLichess')}
      </button>

      {/* Embedded Lichess tournament view. Lichess serves an embeddable iframe
          at /embed/<id>; for a tournament that's the standings/crosstable view.
          TODO(board-client): a fully-native interactive board (playing moves
          via the Lichess Board API) needs a qchess-api proxy that does not
          exist yet — for now we embed for viewing and deeplink out to play. */}
      <section className="overflow-hidden rounded-xl border border-border bg-bg-secondary">
        <iframe
          title={t('tournamentDetail.embedTitle')}
          src={`https://lichess.org/embed/${encodeURIComponent(tour.lichessId)}?theme=dark&bg=dark`}
          className="h-[420px] w-full"
          loading="lazy"
          // Lichess embeds need scripts; sandbox kept permissive enough for the
          // embed to run while blocking top-navigation hijacks.
          sandbox="allow-scripts allow-same-origin allow-popups"
        />
      </section>

      {/* Standings */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-bold text-fg">{t('tournamentDetail.standings')}</h2>
        {standings.length === 0 ? (
          <p className="py-4 text-center text-sm text-fg-muted">
            {t('tournamentDetail.noStandings')}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl bg-bg-secondary">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[11px] uppercase tracking-wide text-fg-muted">
                  <th className="px-3 py-2 text-left font-semibold">{t('tournamentDetail.rank')}</th>
                  <th className="px-3 py-2 text-left font-semibold">{t('tournamentDetail.player')}</th>
                  <th className="px-3 py-2 text-right font-semibold">{t('tournamentDetail.score')}</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((row) => (
                  <tr key={`${row.rank}-${row.username}`} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-mono text-fg-muted">{row.rank}</td>
                    <td className="px-3 py-2 font-semibold text-fg">{row.username}</td>
                    <td className="px-3 py-2 text-right font-mono text-fg">{row.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}
